import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import FluidLine from '@/components/base/fluid-line'
import FluidMask from '@/components/base/fluid-mask'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import { isAcademy, isCorporate } from '@/constants'

import { infographicRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type InfographicProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    paragraph: string
    button: WPLink
    media: MediaElement
  }>

const Infographic = ({
  title,
  paragraph,
  button,
  className,
  media,
  ...props
}: InfographicProps) => {
  return (
    <Section className={cn([className, { dark: isCorporate }])} {...props}>
      <Container
        className={cn([{ 'py-s-spacer-xl md:py-d-spacer-xl': isCorporate }])}
      >
        <FluidLine className="absolute left-0 top-1/2 z-0 -translate-y-1/2" />

        <Grid
          className={cn([
            'relative items-center gap-y-spacing-lg',
            { 'mx-auto max-w-[108rem] lg:gap-spacing-2xl': isCorporate }
          ])}
        >
          <div
            className={cn([
              'col-span-2 md:col-span-3 lg:col-span-6',
              { 'lg:col-span-5': isAcademy }
            ])}
          >
            <div className="relative aspect-square">
              <FluidMask amplitude={10} isVisible={true} square>
                {(ref) => <Media ref={ref} fill {...media} />}
              </FluidMask>
            </div>
          </div>
          <Motion animation={infographicRevealAnimation}>
            {(motionRef) => (
              <div
                ref={motionRef}
                className={cn([
                  'col-span-2 md:col-span-3 lg:col-span-6',
                  'grid gap-y-spacing-sm px-margin md:px-0',
                  { 'lg:col-start-7': isAcademy }
                ])}
              >
                {title ? (
                  <Typography
                    variant="h1"
                    component="h2"
                    className="font-medium"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}

                {paragraph ? (
                  <Typography variant="h4" paragraph data-motion-paragraph>
                    {paragraph}
                  </Typography>
                ) : null}

                {button && button.url ? (
                  <div>
                    <Button
                      asChild
                      isLiquid
                      isInverted={isCorporate}
                      variant="primary"
                      data-motion-button
                    >
                      <Link
                        href={button.url}
                        target={button.target}
                        scroll={false}
                      >
                        <TextEffect>
                          <Typography variant="regular">
                            {button.title}
                          </Typography>
                        </TextEffect>
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </Motion>
        </Grid>
      </Container>
    </Section>
  )
}

export default Infographic
